import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'
import { UserRole } from '@/models/user.model'
import { usersService } from '@/services/usersService'
import { useNotifications } from '@/contexts/NotificationContext'

const createUserSchema = z.object({
    firstName: z.string().min(2, 'First name must be at least 2 characters'),
    lastName: z.string().min(2, 'Last name must be at least 2 characters'),
    email: z.string().email('Please enter a valid email address'),
    password: z.string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
    confirmPassword: z.string(),
    role: z.nativeEnum(UserRole, {
        required_error: 'Please select a role',
    }),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
})

type CreateUserFormData = z.infer<typeof createUserSchema>

interface CreateUserModalProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    defaultRole?: string
}

export function CreateUserModal({ isOpen, onClose, onSuccess, defaultRole }: CreateUserModalProps) {
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const { addNotification } = useNotifications()

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<CreateUserFormData>({
        resolver: zodResolver(createUserSchema),
        defaultValues: {
            role: (defaultRole as UserRole) || UserRole.INSTRUCTOR,
        },
    })

    const onSubmit = async (data: CreateUserFormData) => {
        try {
            setIsLoading(true)

            await usersService.createUser({
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                password: data.password,
                role: data.role,
            })

            addNotification({
                title: 'User created',
                message: `${data.firstName} ${data.lastName} has been successfully created.`,
                type: 'general',
                priority: 'medium',
                isRead: false,
                isArchived: false,
            })

            reset()
            onSuccess()
            onClose()
        } catch (error: any) {
            addNotification({
                title: 'Failed to create user',
                message: error.message || 'An unexpected error occurred',
                type: 'general',
                priority: 'high',
                isRead: false,
                isArchived: false,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="md"
            title={defaultRole === UserRole.INSTRUCTOR ? "Add New Instructor" : "Add New Member"}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-secondary-700 mb-1">
                            First name *
                        </label>
                        <Input
                            {...register('firstName')}
                            className={errors.firstName ? 'border-error-300 focus:ring-error-500' : ''}
                            placeholder="John"
                        />
                        {errors.firstName && (
                            <p className="mt-1 text-xs text-error-600">{errors.firstName.message}</p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-secondary-700 mb-1">
                            Last name *
                        </label>
                        <Input
                            {...register('lastName')}
                            className={errors.lastName ? 'border-error-300 focus:ring-error-500' : ''}
                            placeholder="Doe"
                        />
                        {errors.lastName && (
                            <p className="mt-1 text-xs text-error-600">{errors.lastName.message}</p>
                        )}
                    </div>
                </div>

                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-1">
                        Email address *
                    </label>
                    <Input
                        {...register('email')}
                        type="email"
                        className={errors.email ? 'border-error-300 focus:ring-error-500' : ''}
                        placeholder="john.doe@example.com"
                    />
                    {errors.email && (
                        <p className="mt-1 text-xs text-error-600">{errors.email.message}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="role" className="block text-sm font-medium text-secondary-700 mb-1">
                        Role *
                    </label>
                    <select
                        {...register('role')}
                        className={cn(
                            'w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white',
                            errors.role && 'border-error-300 focus:ring-error-500'
                        )}
                    >
                        <option value={UserRole.INSTRUCTOR}>Instructor</option>
                    </select>
                    {errors.role && (
                        <p className="mt-1 text-xs text-error-600">{errors.role.message}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-1">
                        Password *
                    </label>
                    <div className="relative">
                        <Input
                            {...register('password')}
                            type={showPassword ? 'text' : 'password'}
                            className={cn(
                                'pr-10',
                                errors.password && 'border-error-300 focus:ring-error-500'
                            )}
                            placeholder="Create a strong password"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? (
                                <EyeOff className="h-5 w-5 text-secondary-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-secondary-400" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p className="mt-1 text-xs text-error-600">{errors.password.message}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-secondary-700 mb-1">
                        Confirm password *
                    </label>
                    <div className="relative">
                        <Input
                            {...register('confirmPassword')}
                            type={showConfirmPassword ? 'text' : 'password'}
                            className={cn(
                                'pr-10',
                                errors.confirmPassword && 'border-error-300 focus:ring-error-500'
                            )}
                            placeholder="Confirm your password"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            {showConfirmPassword ? (
                                <EyeOff className="h-5 w-5 text-secondary-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-secondary-400" />
                            )}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <p className="mt-1 text-xs text-error-600">{errors.confirmPassword.message}</p>
                    )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Creating User...
                            </>
                        ) : (
                            'Create User'
                        )}
                    </Button>
                </div>
            </form>
        </Modal>
    )
}
